import { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { lookbookApi } from "@/api/lookbook-api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getErrorMessage } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { parseInviteEmails } from "./invite-emails";

/**
 * "Invite" — Google-Docs-style sharing for a lookbook. The owner enters email
 * addresses; each person is subscribed and emailed the link.
 *
 * Shown only to the workspace's owners, read from the cached workspace list
 * with the same predicate as useWorkspaceRole, but for the lookbook's own
 * workspace rather than whichever one happens to be active. The server checks
 * again; hiding the button is just so nobody is offered one that can only fail.
 */
export function InviteButton({
  opportunityId,
  lookbookId,
  passwordProtected,
  className,
}: {
  opportunityId?: string;
  lookbookId?: string;
  passwordProtected?: boolean;
  className?: string;
}) {
  const { isSignedIn } = useUser();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [message, setMessage] = useState("");

  const isOwner =
    !!opportunityId &&
    workspaces.some(
      (w) =>
        w._id === opportunityId &&
        (w.affiliation_type === "company manager" ||
          w.role === "company manager")
    );

  const send = useMutation({
    mutationFn: () => {
      if (!opportunityId || !lookbookId) throw new Error("Missing lookbook");
      return lookbookApi.invite(
        opportunityId,
        lookbookId,
        parseInviteEmails(emails),
        message.trim() || undefined
      );
    },
    onSuccess: ({ invited, failed, invalid }) => {
      if (invited.length > 0) {
        toast.success(
          invited.length === 1
            ? `Invited ${invited[0]}`
            : `Invited ${invited.length} people`
        );
      }
      const problems = [...invalid, ...failed];
      if (problems.length > 0) {
        toast.error(`Couldn't invite ${problems.join(", ")}`);
        // Keep only the addresses that didn't go, so they can be fixed and retried.
        setEmails(problems.join(", "));
        return;
      }
      setEmails("");
      setMessage("");
      setOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to send invitations"));
    },
  });

  if (!isSignedIn || !isOwner || !lookbookId) return null;

  const count = parseInviteEmails(emails).length;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        className={cn("rounded-full", className)}
        title="Invite people by email"
      >
        <UserPlus />
        Invite
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite people to this lookbook</DialogTitle>
            <DialogDescription>
              They'll get an email with the link and be subscribed, so they hear
              about every company you add.
              {passwordProtected &&
                " The password isn't included — share it with them separately."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="invite-emails">Email addresses</Label>
              <Textarea
                id="invite-emails"
                autoFocus
                rows={3}
                placeholder="name@fund.com, another@fund.com"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-message">Message (optional)</Label>
              <Textarea
                id="invite-message"
                rows={3}
                maxLength={1000}
                placeholder="Here's the shortlist we discussed."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => send.mutate()}
              disabled={count === 0 || send.isPending}
            >
              {send.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {count > 1 ? `Send ${count} invites` : "Send invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InviteButton;
